/**
 * Algolia Insights — theme-side utilities.
 *
 * This file does NOT load search-insights and does NOT call aa(). Those live in the
 * "Algolia Search Insights" custom pixel, which runs in a sandboxed iframe and cannot
 * see this window. See docs/algolia-search-insights-implementation-guide.md.
 */
(function () {
  'use strict';

  var USER_TOKEN_KEY = 'algolia_user_token'; // localStorage — must survive sessions
  var LAST_SEARCH_KEY = 'algolia_last_search'; // sessionStorage
  var LAST_CLICK_KEY = 'algolia_last_click'; // sessionStorage
  var ATTRIBUTION_TTL_MS = 30 * 60 * 1000; // 30 min — spec  3
  var MAX_OBJECT_IDS = 20; // Algolia hard limit per event

  /**
   * Baseline parity — do not rename without an analytics decision.
   *
   * These reproduce the strings and tags the out-of-the-box Algolia Shopify app
   * emitted. The dashboard groups metrics by eventName and segments by
   * algoliaSource, so a renamed event reads as a brand new one: the old trend
   * line flatlines at cutover while the signal itself stays perfectly healthy.
   * See docs/algolia-search-insights-baseline-parity.md.
   */
  var SURFACE = {
    autocomplete: {
      source: ['autocomplete', 'autocomplete-internal'],
      view: 'Autocomplete Items Viewed',
      click: 'Autocomplete Item Selected',
    },
    instantsearch: {
      source: ['instantsearch', 'instantsearch-internal'],
      view: 'Search Results Hits Viewed',
      click: 'Search Results Hit Clicked',
    },
  };
  var CONVERSION_EVENT_NAME = 'Add To Cart';
  var CONVERSION_SUBTYPE = 'addToCart';
  var EVENT_DEDUPE_WINDOW_MS = 2000;
  var recentEvents = new Map();

  function isDuplicateEvent(key) {
    var now = Date.now();
    recentEvents.forEach(function (timestamp, eventKey) {
      if (now - timestamp > EVENT_DEDUPE_WINDOW_MS) recentEvents.delete(eventKey);
    });
    if (recentEvents.has(key)) return true;
    recentEvents.set(key, now);
    return false;
  }

  // ─── Storage helpers (Safari private mode throws on write) ──────────

  function safeGet(store, key) {
    try {
      var raw = store.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function safeSet(store, key, value) {
    try {
      store.setItem(key, JSON.stringify(value));
    } catch {
      /* quota or private mode — analytics must never break the storefront */
    }
  }

  // ─── userToken ──────────────────────────────────────────────────────

  function getUserToken() {
    var token = null;
    try {
      token = localStorage.getItem(USER_TOKEN_KEY);
    } catch {
      /* fall through */
    }
    if (token) return token;

    token =
      window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : 'anon-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);

    try {
      localStorage.setItem(USER_TOKEN_KEY, token);
    } catch {
      /* token is per-page-view if storage is unavailable — acceptable degradation */
    }
    return token;
  }

  // ─── Config ─────────────────────────────────────────────────────────

  function getConfig() {
    var c = window.__ALGOLIA_CONFIG;
    if (!c || !c.appId || !c.searchOnlyApiKey) return null;
    return { appId: c.appId, searchApiKey: c.searchOnlyApiKey };
  }

  // ─── Publish ────────────────────────────────────────────────────────

  function publishInsightsEvent(evt) {
    if (!window.Shopify || !window.Shopify.analytics) return;

    var cfg = getConfig();
    if (!cfg) return;

    if (!Array.isArray(evt.objectIDs) || evt.objectIDs.length === 0) return;
    if (!evt.index) return;

    evt.userToken = getUserToken();
    window.Shopify.analytics.publish('algolia_insights_event', { config: cfg, event: evt });
  }

  /** @param {string} surface  'autocomplete' | 'instantsearch' */
  function publishView(surface, indexName, objectIDs) {
    var s = SURFACE[surface];
    if (!s) return;

    publishInsightsEvent({
      eventType: 'view',
      eventName: s.view,
      algoliaSource: s.source,
      index: indexName,
      objectIDs: objectIDs.slice(0, MAX_OBJECT_IDS),
    });
  }

  // Also stores the click, so an add-to-cart on the PDP the shopper lands on
  // can still resolve back to the search that produced it.
  function publishClick(surface, indexName, objectID, position, queryID) {
    var s = SURFACE[surface];
    if (!s || !position) return;

    var eventKey = ['click', surface, indexName, objectID, queryID, position].join('|');
    if (isDuplicateEvent(eventKey)) return;

    recordClick(objectID, indexName, queryID, surface);
    publishInsightsEvent({
      eventType: 'click',
      eventName: s.click,
      algoliaSource: s.source,
      index: indexName,
      objectIDs: [String(objectID)],
      positions: [position],
      queryID: queryID,
    });
  }

  // eventSubtype 'addToCart' is what separates this from a generic conversion
  // in the dashboard's breakdown; the baseline sent it and so must this.
  function publishConversion(surface, indexName, objectID, queryID) {
    var s = SURFACE[surface] || SURFACE.instantsearch;
    var eventKey = ['conversion', indexName, objectID, queryID].join('|');
    if (isDuplicateEvent(eventKey)) return;

    publishInsightsEvent({
      eventType: 'conversion',
      eventSubtype: CONVERSION_SUBTYPE,
      eventName: CONVERSION_EVENT_NAME,
      algoliaSource: s.source,
      index: indexName,
      objectIDs: [String(objectID)],
      queryID: queryID,
    });
  }

  // ─── Attribution ────────────────────────────────────────────────────

  /**
   * Called when the query text changes, so a new search doesn't inherit the
   * previous one's attribution.
   *
   * The click record survives deliberately. It matches a single objectID and
   * expires on its own TTL, and it is what carries attribution across the
   * navigation to a PDP — clearing it here would break the
   * click-through-then-add-to-cart journey, the most valuable path to attribute.
   */
  function clearSearchRecords() {
    try {
      sessionStorage.removeItem(LAST_SEARCH_KEY);
    } catch {
      /* private mode — nothing stored to clear */
    }
  }

  /** Full reset — search records and the pending click. */
  function clearAttribution() {
    clearSearchRecords();
    try {
      sessionStorage.removeItem(LAST_CLICK_KEY);
    } catch {
      /* private mode */
    }
  }

  /**
   * Keyed by index rather than a single "last search" slot: the ALL tab fires
   * five searches in parallel and they resolve in nondeterministic order, so
   * one slot would usually end up holding the pages index. A product click
   * would then look up a record that never contained it and lose attribution.
   */
  /**
   * A queryID is a bare 32-char hex token. Responses on some indices arrive
   * with a literal "queryID:" prefix, which Algolia then rejects with a 422 —
   * invisibly, because aa() is fire-and-forget and never throws on rejection.
   * One malformed character silently voids every attributed event, so it is
   * normalised here rather than trusted.
   *
   * This is a guard, not a fix: the prefix should not be arriving at all.
   * Compare the raw search response before assuming this handles it.
   */
  function normalizeQueryID(queryID) {
    if (typeof queryID !== 'string') return null;
    var cleaned = queryID.replace(/^queryID:/i, '').trim();
    return /^[a-f0-9]{32}$/i.test(cleaned) ? cleaned : null;
  }

  // key separates records that share an index (recipes and blogs both query
  // shopify_articles); without it the second one written would replace the first.
  function recordSearch(indexName, result, page, hitsPerPage, key) {
    if (!result) return;

    // No queryID means clickAnalytics is off or this was a facet-only query.
    var queryID = normalizeQueryID(result.queryID);
    if (!queryID) {
      if (result.queryID) {
        console.warn('[AlgoliaInsights] unusable queryID on ' + indexName + ':', result.queryID);
      }
      return;
    }

    var all = safeGet(sessionStorage, LAST_SEARCH_KEY) || {};
    var hits = result.hits || [];

    // objectIDs and productIDs are parallel arrays over the same hits.
    //
    // The products index is variant-level: objectID is the variant, hit.id is
    // the product. Algolia must receive the objectID it indexed, but shoppers
    // convert at the product level — someone can search up the Blue variant and
    // add Red from the PDP. Keeping both lets attribution match on either while
    // still reporting the indexed objectID. Content indices have no separate
    // product id, so productID falls back to objectID and both lookups agree.
    all[key || indexName] = {
      queryID: queryID,
      indexName: indexName,
      objectIDs: hits.map(function (h) {
        return String(h.objectID);
      }),
      productIDs: hits.map(function (h) {
        return String(h.id || h.objectID);
      }),
      offset: (page || 0) * (hitsPerPage || result.hitsPerPage || 0),
      ts: Date.now(),
    };

    // Drop expired records so the entry can't grow without bound across a session.
    Object.keys(all).forEach(function (k) {
      if (!isFresh(all[k])) delete all[k];
    });

    safeSet(sessionStorage, LAST_SEARCH_KEY, all);
  }

  /** The freshest non-expired record for a given index, or null. */
  function getSearchFor(indexName) {
    var all = safeGet(sessionStorage, LAST_SEARCH_KEY) || {};
    var record = all[indexName];
    return isFresh(record) ? record : null;
  }

  // Index of a hit within a record, matching on either the Algolia objectID or
  // the Shopify product id. Callers hold one or the other depending on whether
  // they came from a rendered card or from a cart line item.
  function indexOfHit(record, anyID) {
    if (!record) return -1;
    var id = String(anyID);
    var i = record.objectIDs.indexOf(id);
    if (i !== -1) return i;
    return record.productIDs ? record.productIDs.indexOf(id) : -1;
  }

  // The freshest live record containing this id. Never guesses, so a product
  // absent from every live result set stays unattributed.
  //
  // autocomplete and instantsearch can hold records for the SAME index at the
  // SAME time (e.g. the header search bar open on the results page), each
  // under its own key (see recordSearch's `key` param / 'autocomplete:' prefix
  // below) precisely so neither overwrites the other. A grid click must only
  // ever resolve against a grid record — matching an autocomplete record here
  // would attribute the click to the wrong surface (and the wrong position,
  // since offsets differ). Pass excludeAutocomplete=true from any instantsearch
  // caller; autocomplete has no equivalent need since it looks its own record
  // up directly by key via getSearchFor, never through this scan.
  function findSearchFor(anyID, excludeAutocomplete) {
    var all = safeGet(sessionStorage, LAST_SEARCH_KEY) || {};
    var best = null;

    Object.keys(all).forEach(function (k) {
      if (excludeAutocomplete && k.indexOf('autocomplete:') === 0) return;
      var r = all[k];
      if (!isFresh(r) || indexOfHit(r, anyID) === -1) return;
      if (!best || r.ts > best.ts) best = r;
    });

    return best;
  }

  /** 1-based position within the full result set, or null. */
  function getPositionOf(record, anyID) {
    var i = indexOfHit(record, anyID);
    return i === -1 ? null : (record.offset || 0) + i + 1;
  }

  /** The objectID Algolia indexed for this hit — what events must report. */
  function getObjectIdOf(record, anyID) {
    var i = indexOfHit(record, anyID);
    return i === -1 ? null : record.objectIDs[i];
  }

  // Stores the product id alongside the objectID so a conversion on the PDP —
  // which only knows Shopify ids, and may be for a different variant than the
  // one clicked — can still match this click.
  function recordClick(objectID, indexName, queryID, surface) {
    var record = getSearchFor(indexName);
    var i = indexOfHit(record, objectID);

    safeSet(sessionStorage, LAST_CLICK_KEY, {
      queryID: queryID,
      indexName: indexName,
      objectID: String(objectID),
      productID: i === -1 ? String(objectID) : record.productIDs[i],
      surface: surface || 'instantsearch',
      ts: Date.now(),
    });
  }

  function isFresh(record) {
    return !!record && Date.now() - record.ts < ATTRIBUTION_TTL_MS;
  }

  /**
   * Attribution for a product about to convert. Accepts a Shopify product id or
   * an Algolia objectID; returns the objectID the event must report. Order:
   *   1. clicked through from search, now on its PDP
   *   2. converting directly off a results page
   *   3. neither, or expired — null, never a fabricated queryID
   */
  function resolveAttribution(anyID) {
    var id = String(anyID);

    var click = safeGet(sessionStorage, LAST_CLICK_KEY);
    if (isFresh(click) && click.queryID && (click.objectID === id || click.productID === id)) {
      return {
        queryID: click.queryID,
        indexName: click.indexName,
        surface: click.surface,
        objectID: click.objectID,
      };
    }

    // No click record — converting straight off a results page, which is the
    // instantsearch surface by definition. excludeAutocomplete=true: a bare
    // autocomplete impression (shown but never clicked) must not be mistaken
    // for a results-page conversion.
    var search = findSearchFor(id, true);
    if (search) {
      return {
        queryID: search.queryID,
        indexName: search.indexName,
        surface: 'instantsearch',
        objectID: getObjectIdOf(search, id),
      };
    }

    return null;
  }

  // ─── Public API ─────────────────────────────────────────────────────

  window.AlgoliaInsights = {
    getUserToken: getUserToken,
    // Emit through these three, not publishInsightsEvent — they're what keep
    // eventName, algoliaSource and eventSubtype matching the baseline.
    publishView: publishView,
    publishClick: publishClick,
    publishConversion: publishConversion,
    publishInsightsEvent: publishInsightsEvent, // escape hatch
    recordSearch: recordSearch,
    recordClick: recordClick,
    clearSearchRecords: clearSearchRecords,
    clearAttribution: clearAttribution,
    getSearchFor: getSearchFor,
    findSearchFor: findSearchFor,
    getPositionOf: getPositionOf,
    getObjectIdOf: getObjectIdOf,
    resolveAttribution: resolveAttribution,
    MAX_OBJECT_IDS: MAX_OBJECT_IDS,
  };

  // Announce config early. Harmless if the pixel isn't listening yet —
  // every event carries config too (see guide  2.3).
  document.addEventListener('DOMContentLoaded', function () {
    var cfg = getConfig();
    if (cfg && window.Shopify && window.Shopify.analytics) {
      /* window.Shopify.analytics.publish('algolia_config_ready', { config: cfg }); */
    }
  });
})();
